import React, { useEffect, useCallback } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { MdClose } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import useTimeSheetApi from "../../../api/timesheet";
import { ALlEmployeeskillResponse, CompentencyArea, EmployeeSkillsResponse } from "../../../model/timesheet";
import { toast } from "react-toastify";

type SkillForm = {
  skills: Skill[];
};

type Skill = {
  display_row_id: number | null;
  employee_skill_set_id: number | null;
  knowledge_id: number | null;
  selected_skills: string;
  level_id: number | null;
};

export interface SkillData {
  skill_id: number;
  skill_name: string;
  knowledge_id: number;
}

export interface SkillLevel {
  level_id: number;
  level_name: string;
}

const SkillsManagement = () => {
  const navigate = useNavigate();
  const { fetchGetEmployeeskill, fetchSaveEmployeeskill } = useTimeSheetApi();
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<SkillForm>({
    defaultValues: {
      skills: [],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "skills",
  });

  const [competencyAreas, setCompetencyAreas] = React.useState<CompentencyArea[] | null>(null);
  const [skillOptions, setSkillOptions] = React.useState<SkillData[] | null>(null);
  const [levels, setLevels] = React.useState<SkillLevel[] | null>(null);

  const getEmployeeSkillList = useCallback(async () => {
    try {
      const data: ALlEmployeeskillResponse | null = await fetchGetEmployeeskill();
      if (data === null) {
        throw new Error("No data received from fetchGetEmployeeskill");
      }

      console.log('selectedSkill', data.data.employee_skills);

      setValue("skills", data.data.employee_skills);
      setCompetencyAreas(data.compentency_areas ?? null);
      setSkillOptions(
        data.compentency_areas
          .flatMap((area) =>
            (area.skills || []).map((skill) => ({
              ...skill,
              knowledge_id: area.knowledge_id,
            })),
          )
          .filter((skill): skill is SkillData & { knowledge_id: number } => skill !== undefined),
      );
      setLevels(data.skill_levels);
      setValue("skills", data.data.employee_skills);
    } catch (error) {
      console.error(error);
    }
  }, [fetchGetEmployeeskill]);

  useEffect(() => {
    getEmployeeSkillList();
  }, []);

  const addSkill = () => {
    append({
      knowledge_id: null,
      selected_skills: "",
      level_id: null,
      display_row_id: null,
      employee_skill_set_id: null,
    });
  };

  const onSubmit = async (data: SkillForm) => {
    console.log(data);

    try {
      const responseData = await fetchSaveEmployeeskill(data as EmployeeSkillsResponse);
      if (responseData) {
        toast.success("Employee skill saved successfully");
      }
      // console.log(responseData);
      return responseData;
    } catch (error) {
      // setError(error.message);
    }
  };

  return (
    <div className='w-full'>
      <div className="mb-4">
        <button
          onClick={() => navigate("/")}
          className="px-4 py-2 bg-gray-900 text-white rounded-lg focus:outline-none hover:bg-gray-600"
        >
          Back to Home
        </button>
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className='rounded-lg overflow-auto'>
          <table className='w-full md:w-auto'>
            <thead>
              <tr className='bg-gray-100 text-gray-700'>
                <th className='py-2 px-4 text-center'>Competency Area</th>
                <th className='py-2 px-4 text-center'>Skills</th>
                <th className='py-2 px-4 text-center'>Level</th>
                <th className='py-2 px-4 text-center'>Selected Skills</th>
                <th className='py-2 px-4 text-center'>Actions</th>
              </tr>
            </thead>
            <tbody>
              {fields.map((field, index) => (
                <tr key={field.id} className='border-b'>
                  <td className='py-2 px-4' style={{ width: '300px' }}>
                    <Controller
                      control={control}
                      name={`skills.${index}.knowledge_id`}
                      rules={{ required: "Competency Area is required" }}
                      render={({ field }) => (
                        <select
                          {...field}
                          value={field.value?.toString() || ""}
                          onChange={(e) => {
                            setValue(`skills.${index}.knowledge_id`, parseInt(e.target.value, 10));
                            field.onChange(e);
                          }}
                          className={`w-full p-2 border rounded max-w-md ${errors.skills?.[index]?.knowledge_id ? "border-red-500" : ""
                            }`}
                        >
                          <option value=''>Select Competency Area</option>
                          {competencyAreas?.map((area) => (
                            <option key={area.knowledge_id} value={area.knowledge_id}>
                              {area.knowledge_name}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                    {errors.skills?.[index]?.knowledge_id && (
                      <p className='text-red-500 text-xs mt-1'>
                        {errors.skills[index]?.knowledge_id?.message}
                      </p>
                    )}
                  </td>
                  <td className='py-2 px-4' style={{ width: '300px' }}>
                    <Controller
                      control={control}
                      name={`skills.${index}.selected_skills`}
                      rules={{ required: "Skills are required" }}
                      render={({ field }) => (
                        <select
                          {...field}
                          multiple
                          value={field.value ? field.value.split(",") : []}
                          onChange={(e) => {
                            const selectedOptions = Array.from(e.target.selectedOptions).map(
                              (option) => option.value,
                            );
                            field.onChange(selectedOptions.join(","));
                          }}
                          className={`w-full p-2 border rounded ${errors.skills?.[index]?.selected_skills ? "border-red-500" : ""
                            }`}
                        >
                          {skillOptions
                            ?.filter(
                              (item) => item.knowledge_id === Number(watch(`skills.${index}.knowledge_id`)),
                            )
                            ?.map((s) => (
                              <option key={s.skill_id} value={s.skill_name}>
                                {s.skill_name}
                              </option>
                            ))}
                        </select>
                      )}
                    />
                    {errors.skills?.[index]?.selected_skills && (
                      <p className='text-red-500 text-xs mt-1'>{errors.skills[index]?.selected_skills?.message}</p>
                    )}
                  </td>
                  <td className='py-2 px-4' style={{ width: '150px' }}>
                    <Controller
                      control={control}
                      name={`skills.${index}.level_id`}
                      rules={{ required: "Skill Level is required" }}
                      render={({ field }) => (
                        <select
                          {...field}
                          value={field.value?.toString() || ""}
                          className={`w-full p-2 border rounded ${errors.skills?.[index]?.level_id ? "border-red-500" : ""
                            }`}
                        >
                          <option value=''>Select Level</option>
                          {levels?.map((level) => (
                            <option key={level.level_id} value={level.level_id}>
                              {level.level_name}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                    {errors.skills?.[index]?.level_id && (
                      <p className='text-red-500 text-xs mt-1'>{errors.skills[index]?.level_id?.message}</p>
                    )}
                  </td>
                  <td className='py-2 px-4' style={{ width: '300px' }}>
                    <textarea
                      value={watch(`skills.${index}.selected_skills`)}
                      className={`w-full p-2 border rounded`}
                    ></textarea>
                  </td>
                  <td className='flex justify-center items-center align-middle mt-8'>
                    <button
                      type='button'
                      onClick={() => remove(index)}
                      className='text-red-500 hover:text-red-700'
                    >
                      <MdClose size={20} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className='mt-4 flex justify-between'>
          <button
            type='button'
            onClick={addSkill}
            className='bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-600 flex items-center'
          >
            Add Skills
          </button>
          <button
            type='button'
            onClick={() => {
              setValue("skills", []);
            }}
            className='bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600 mr-2'
          >
            Reset all skills
          </button>
          <div>
            <button
              type='submit'
              className='bg-green-500 text-white py-2 px-4 rounded hover:bg-green-600 mr-2'
            >
              Save
            </button>

          </div>
        </div>
      </form >
    </div >
  );
};

export default SkillsManagement;
